import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  signupSchema,
  type SignupInput,
  BRAND,
  PASSWORD_MIN_LENGTH,
  passwordStrength,
} from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, ApiError } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { BrandLogo } from '@/components/brand/brand-logo';
import { Check, Eye, EyeOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TurnstileWidget, HoneypotField } from '@/components/turnstile-widget';
import { usePublicConfig } from '@/lib/public-config';
import { captureReferralParams, getCarriedWebsiteUrl, getReferralPayload } from '@/lib/referral';
import { captureNextParam, consumeNextPath, getNextPath } from '@/lib/deeplink';

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-sidebar p-12 text-white lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <BrandLogo inverted size="md" to="/" className="relative" />
        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">{BRAND.tagline}</h2>
          <ul className="mt-8 space-y-3.5">
            {[
              'Website check-up for US trade businesses',
              'Find what is costing you customers',
              'Fix it this week on the site you already have',
            ].map((f) => (
              <li key={f} className="flex items-center gap-3 text-[15px] text-blue-100">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/30">
                  <Check className="h-3 w-3 text-white" />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-[13px] text-sidebar-text">
          Built for contractors who want more from the website they already run.
        </p>
      </div>

      <div className="flex items-center justify-center bg-bg-app p-6">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

function MobileAuthBrand() {
  return (
    <div className="mb-7 lg:hidden">
      <BrandLogo size="md" to="/" />
    </div>
  );
}

function PasswordField({
  registerProps,
  error,
  value,
}: {
  registerProps: ReturnType<ReturnType<typeof useForm>['register']>;
  error?: string;
  value: string;
}) {
  const [show, setShow] = useState(false);
  const strength = useMemo(() => passwordStrength(value || ''), [value]);
  return (
    <div className="space-y-1.5">
      <div className="relative">
        <Input
          label="Password"
          type={show ? 'text' : 'password'}
          error={error}
          helper={`At least ${PASSWORD_MIN_LENGTH} characters.`}
          {...registerProps}
          required
        />
        <button
          type="button"
          className="absolute right-3 top-[34px] text-text-muted hover:text-text-secondary"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {value.length > 0 && (
        <p className="text-[12px] text-text-muted">
          Strength: <span className="font-medium text-text-secondary">{strength.label}</span>
        </p>
      )}
    </div>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const googleSignIn = publicConfig?.googleSignIn ?? false;
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);

  const { user: signedInUser, loading: authLoading } = useAuth();
  const initialCheck = useRef(false);

  useEffect(() => {
    captureReferralParams();
    const hadNext = captureNextParam();
    if (authLoading || initialCheck.current) return;
    initialCheck.current = true;
    // Already signed in and arriving with a deep link: go straight to the kit page.
    if (hadNext && signedInUser?.user) navigate({ to: consumeNextPath() });
  }, [authLoading, signedInUser?.user, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      website: '',
      website_url: getCarriedWebsiteUrl(),
      accept_terms: false as unknown as true,
      email: '',
      password: '',
      name: '',
      business_name: '',
    },
  });

  const passwordValue = watch('password') || '';

  const onSubmit = async (data: SignupInput) => {
    if (turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      const res = await api<{ needs_verification?: boolean; debug_verify_code?: string }>('/api/v1/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          ...getReferralPayload(),
          turnstile_token: turnstileToken || undefined,
        }),
      });
      if (res.debug_verify_code) {
        try {
          sessionStorage.setItem('cgk_debug_verify_code', res.debug_verify_code);
        } catch {
          /* ignore */
        }
      }
      await refresh();
      navigate({ to: res.needs_verification === false ? '/onboarding' : '/verify-email' });
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'EMAIL_EXISTS') {
        setError('email', {
          message: 'Email already registered. Log in instead.',
        });
      } else {
        setError('email', { message: err.message || 'Sign up failed. Please try again.' });
      }
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Create your free account</h1>
      <p className="mt-1.5 text-sm text-text-secondary">No credit card. Free while we launch.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
        <Input label="Email" type="email" error={errors.email?.message} {...register('email')} required />
        <PasswordField
          registerProps={register('password')}
          error={errors.password?.message}
          value={passwordValue}
        />
        <input type="hidden" {...register('timezone')} />
        <input type="hidden" {...register('website_url')} />
        <HoneypotField {...register('website')} />
        <label className="flex items-start gap-2.5 text-sm text-text-secondary">
          <input type="checkbox" className="mt-1 rounded border-border" {...register('accept_terms')} />
          <span>
            I agree to the{' '}
            <Link to="/terms" className="font-medium text-brand hover:underline">
              Terms
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="font-medium text-brand hover:underline">
              Privacy Policy
            </Link>
          </span>
        </label>
        {errors.accept_terms && <p className="text-[13px] text-danger">{errors.accept_terms.message}</p>}
        {turnstileSiteKey && <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />}
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={Boolean(turnstileSiteKey && !turnstileToken)}
          className="w-full"
          size="lg"
        >
          Create account
        </Button>
      </form>
      {googleSignIn && (
        <p className="mt-4 text-center text-xs text-text-muted">Google sign-in is available after email verification setup.</p>
      )}
      <p className="mt-5 text-center text-sm text-text-secondary">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-brand hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [showTurnstile, setShowTurnstile] = useState(false);
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const { user: signedInUser, loading: authLoading } = useAuth();
  const initialCheck = useRef(false);

  useEffect(() => {
    const hadNext = captureNextParam();
    if (authLoading || initialCheck.current) return;
    initialCheck.current = true;
    // Only for visitors who were already signed in when the page opened (not after submitting).
    if (hadNext && signedInUser?.user?.emailVerified !== false && signedInUser?.user) {
      navigate({ to: consumeNextPath() });
    }
  }, [authLoading, signedInUser?.user, navigate]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { email: '', password: '', website: '' },
  });

  const onSubmit = async (data: { email: string; password: string; website?: string }) => {
    if (showTurnstile && turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      const res = await api<{ email_verified?: boolean }>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          website: data.website,
          turnstile_token: turnstileToken || undefined,
        }),
      });
      const me = await refresh();
      if (res.email_verified === false || me?.user?.emailVerified === false) {
        navigate({ to: '/verify-email' });
        return;
      }
      if (me?.settings && !me.settings.onboardingComplete && getNextPath()) {
        // Finish onboarding first; the deep link is consumed when it completes.
        navigate({ to: '/onboarding' });
        return;
      }
      navigate({ to: consumeNextPath() });
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'BOT_CHECK' || err.message?.includes('security check')) {
        setShowTurnstile(true);
      }
      setError('email', { message: err.message || 'Invalid email or password' });
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
      <p className="mt-1.5 text-sm text-text-secondary">Sign in to your {BRAND.name} account.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
        <Input label="Email" type="email" error={errors.email?.message} {...register('email')} required />
        <Input label="Password" type="password" {...register('password')} required />
        <HoneypotField {...register('website')} />
        <div className="text-right">
          <Link to="/forgot-password" className="text-[13px] font-medium text-brand hover:underline">
            Forgot password?
          </Link>
        </div>
        {showTurnstile && turnstileSiteKey && (
          <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
        )}
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={Boolean(showTurnstile && turnstileSiteKey && !turnstileToken)}
          className="w-full"
          size="lg"
        >
          Sign in
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-text-secondary">
        No account?{' '}
        <Link to="/signup" className="font-medium text-brand hover:underline">
          Sign up free
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const { data: publicConfig } = usePublicConfig();
  const turnstileSiteKey = publicConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm({
    defaultValues: { email: '', website: '' },
  });

  const onSubmit = async (data: { email: string; website?: string }) => {
    if (turnstileSiteKey && !turnstileToken) {
      setError('email', { message: 'Please complete the security check.' });
      return;
    }
    try {
      await api('/api/v1/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          website: data.website,
          turnstile_token: turnstileToken || undefined,
        }),
      });
    } catch (e) {
      const err = e as ApiError;
      setError('email', { message: err.message || 'Could not send reset link. Try again later.' });
      throw e;
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
      <p className="mt-1.5 text-sm text-text-secondary">
        Enter your email and we&apos;ll send a link if an account exists. The link works once and expires in 60 minutes.
      </p>
      {isSubmitSuccessful ? (
        <div className="mt-7 rounded-xl border border-success-border bg-success-subtle p-4 text-sm text-success">
          If an account exists for that email, a reset link is on its way. Check your inbox.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
          <Input
            label="Email"
            type="email"
            error={errors.email?.message}
            {...register('email', { required: true })}
            required
          />
          <HoneypotField {...register('website')} />
          {turnstileSiteKey && <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={Boolean(turnstileSiteKey && !turnstileToken)}
            className="w-full"
            size="lg"
          >
            Send reset link
          </Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-text-secondary">
        Remembered it?{' '}
        <Link to="/login" className="font-medium text-brand hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { password: '' },
  });
  const passwordValue = watch('password') || '';

  const onSubmit = async (data: { password: string }) => {
    if (data.password.length < PASSWORD_MIN_LENGTH) {
      setError('password', { message: `At least ${PASSWORD_MIN_LENGTH} characters.` });
      return;
    }
    try {
      await api('/api/v1/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password: data.password }),
      });
      navigate({ to: '/login' });
    } catch (e) {
      setError('password', { message: (e as ApiError).message || 'That reset link is invalid or expired.' });
    }
  };

  return (
    <AuthLayout>
      <MobileAuthBrand />
      <h1 className="text-2xl font-semibold tracking-tight">Choose a new password</h1>
      <p className="mt-1.5 text-sm text-text-secondary">Enter a new password for your account.</p>
      {!token ? (
        <div className="mt-7 rounded-xl border border-danger-border bg-danger-subtle p-4 text-sm text-danger">
          This reset link is missing its token. Request a new link from the{' '}
          <Link to="/forgot-password" className="font-medium underline">
            forgot password
          </Link>{' '}
          page.
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-7 space-y-4">
          <PasswordField
            registerProps={register('password', { required: true, minLength: PASSWORD_MIN_LENGTH })}
            error={errors.password?.message}
            value={passwordValue}
          />
          <Button type="submit" loading={isSubmitting} className="w-full" size="lg">
            Update password
          </Button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-text-secondary">
        <Link to="/login" className="font-medium text-brand hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [debugCode, setDebugCode] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('cgk_debug_verify_code');
      if (stored) {
        setDebugCode(stored);
        setCode(stored);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      setLoading(true);
      try {
        await api('/api/v1/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
        await refresh();
        navigate({ to: '/onboarding' });
      } catch (e) {
        setError((e as ApiError).message || 'That link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, navigate, refresh]);

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/api/v1/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ code, email: user?.user?.email }),
      });
      try {
        sessionStorage.removeItem('cgk_debug_verify_code');
      } catch {
        /* ignore */
      }
      await refresh();
      navigate({ to: '/onboarding' });
    } catch (err) {
      setError((err as ApiError).message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResendMsg('');
    try {
      const res = await api<{ debug_verify_code?: string }>('/api/v1/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: user?.user?.email }),
      });
      if (res.debug_verify_code) {
        setDebugCode(res.debug_verify_code);
        setCode(res.debug_verify_code);
        try {
          sessionStorage.setItem('cgk_debug_verify_code', res.debug_verify_code);
        } catch {
          /* ignore */
        }
        setResendMsg('Email sending is off — code shown below (also in Worker logs).');
      } else {
        setResendMsg('Code sent. Check your inbox.');
      }
      setCooldown(60);
    } catch (err) {
      setResendMsg((err as ApiError).message || 'Could not resend yet.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-bg-surface p-8 shadow-card">
        <div className="mx-auto mb-4 flex justify-center">
          <BrandLogo size="lg" to="/" showName={false} />
        </div>
        <h1 className="text-center text-xl font-semibold">{BRAND.name}</h1>
        <h2 className="mt-2 text-center text-lg font-semibold">Verify your email</h2>
        {token ? (
          <p className="mt-4 text-center text-sm text-text-secondary">
            {loading ? 'Verifying…' : error || 'Opening your account…'}
          </p>
        ) : (
          <>
            <p className="mt-2 text-center text-sm text-text-secondary">
              Enter the 6-digit code we sent to {user?.user?.email || 'your email'}. It expires in 30 minutes.
            </p>
            {debugCode && (
              <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm text-amber-950">
                Testing mode (email off): your code is <strong className="tracking-widest">{debugCode}</strong>
              </p>
            )}
            <form onSubmit={submitCode} className="mt-6 space-y-4">
              <Input
                label="Verification code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={error || undefined}
                required
              />
              <Button type="submit" loading={loading} className="w-full" size="lg">
                Verify email
              </Button>
            </form>
            <div className="mt-4 flex flex-col items-center gap-2 text-sm">
              <button
                type="button"
                className="font-medium text-brand hover:underline disabled:text-text-muted"
                onClick={resend}
                disabled={cooldown > 0}
              >
                {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
              </button>
              {resendMsg && <p className="text-text-muted">{resendMsg}</p>}
              <Link to="/signup" className="text-text-secondary hover:underline">
                Use a different email
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
